import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { API } from '../App';
import { AuthContext } from '../App';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { BookOpen, FileText, Video, Plus, Edit, Trash2, Upload, Download, GraduationCap, Users, Store, Globe, ExternalLink } from 'lucide-react';

const LANGUAGES = [
  { code: 'en', name: 'English', flag: '\u{1F1FA}\u{1F1F8}' },
  { code: 'fr', name: 'Fran\u00e7ais', flag: '\u{1F1EB}\u{1F1F7}' },
  { code: 'es', name: 'Espa\u00f1ol', flag: '\u{1F1EA}\u{1F1F8}' },
  { code: 'de', name: 'Deutsch', flag: '\u{1F1E9}\u{1F1EA}' },
];

const getYouTubeEmbedUrl = (url) => {
  if (!url) return null;
  const match = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]+)/);
  return match ? `https://www.youtube.com/embed/${match[1]}` : null;
};

const KnowledgePage = () => {
  const { t, i18n } = useTranslation();
  const { user } = useContext(AuthContext);
  const currentLang = i18n.language?.split('-')[0] || 'en';
  const isAdmin = user?.role === 'admin';

  const [tutorials, setTutorials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTutorial, setEditingTutorial] = useState(null);
  const [uploading, setUploading] = useState(false);

  const emptyForm = {
    title: { en: '' },
    description: { en: '' },
    audience: 'attendee',
    type: 'document',
    files: {},
    video_urls: { en: '' },
    order: 0,
  };
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    fetchTutorials();
  }, []);

  const fetchTutorials = async () => {
    try {
      const res = await axios.get(`${API}/tutorials`);
      setTutorials(res.data);
    } catch (error) {
      console.error('Failed to fetch tutorials:', error);
    } finally {
      setLoading(false);
    }
  };

  const getLocalizedText = (obj) => {
    if (!obj || typeof obj !== 'object') return '';
    return obj[currentLang] || obj['en'] || Object.values(obj).find(v => v) || '';
  };

  const getLocalizedFile = (files) => {
    if (!files || typeof files !== 'object') return null;
    return files[currentLang] || files['en'] || Object.values(files).find(v => v) || null;
  };

  const getLocalizedVideoUrl = (videoUrls) => {
    if (!videoUrls || typeof videoUrls !== 'object') return null;
    return videoUrls[currentLang] || videoUrls['en'] || Object.values(videoUrls).find(v => v) || null;
  };

  const handleFileUpload = async (langCode) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.pdf,.ppt,.pptx';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      setUploading(true);
      try {
        const formData = new FormData();
        formData.append('file', file);
        const token = localStorage.getItem('token');
        const res = await axios.post(`${API}/tutorials/upload`, formData, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
        });
        setForm(prev => ({
          ...prev,
          files: {
            ...prev.files,
            [langCode]: { path: res.data.storage_path, filename: res.data.original_filename, content_type: res.data.content_type }
          }
        }));
        toast.success(`File uploaded for ${langCode.toUpperCase()}`);
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Upload failed');
      } finally {
        setUploading(false);
      }
    };
    input.click();
  };

  const handleSave = async () => {
    if (!getLocalizedText(form.title)) {
      toast.error('Please enter at least an English title');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
      if (editingTutorial) {
        await axios.put(`${API}/tutorials/${editingTutorial.id}`, form, { headers });
        toast.success('Tutorial updated');
      } else {
        await axios.post(`${API}/tutorials`, form, { headers });
        toast.success('Tutorial created');
      }
      setShowModal(false);
      setEditingTutorial(null);
      setForm(emptyForm);
      fetchTutorials();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save tutorial');
    }
  };

  const handleDelete = async (tutorialId) => {
    if (!window.confirm('Are you sure you want to delete this tutorial?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/tutorials/${tutorialId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Tutorial deleted');
      fetchTutorials();
    } catch (error) {
      toast.error('Failed to delete tutorial');
    }
  };

  const openEditModal = (tutorial) => {
    setEditingTutorial(tutorial);
    setForm({
      title: tutorial.title || { en: '' },
      description: tutorial.description || { en: '' },
      audience: tutorial.audience || 'attendee',
      type: tutorial.type || 'document',
      files: tutorial.files || {},
      video_urls: tutorial.video_urls || { en: '' },
      order: tutorial.order || 0,
    });
    setShowModal(true);
  };

  const attendeeTutorials = tutorials.filter(t => t.audience === 'attendee');
  const exhibitorTutorials = tutorials.filter(t => t.audience === 'exhibitor');

  const TutorialCard = ({ tutorial }) => {
    const title = getLocalizedText(tutorial.title);
    const description = getLocalizedText(tutorial.description);
    const file = getLocalizedFile(tutorial.files);
    const videoUrl = getLocalizedVideoUrl(tutorial.video_urls);
    const embedUrl = getYouTubeEmbedUrl(videoUrl);

    return (
      <Card className="overflow-hidden" data-testid={`tutorial-card-${tutorial.id}`}>
        {tutorial.type === 'video' && embedUrl && (
          <div className="aspect-video">
            <iframe
              src={embedUrl}
              title={title}
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        )}
        <CardContent className="p-5">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                {tutorial.type === 'video' ? (
                  <Video className="h-4 w-4 text-red-500 flex-shrink-0" />
                ) : (
                  <FileText className="h-4 w-4 text-blue-500 flex-shrink-0" />
                )}
                <h3 className="font-semibold text-slate-900">{title}</h3>
              </div>
              {description && (
                <p className="text-sm text-slate-600 mb-3">{description}</p>
              )}
              {tutorial.type === 'document' && file && (
                <a
                  href={`${API}/tutorials/files/${file.path}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-sm text-primary hover:underline font-medium"
                  data-testid={`tutorial-download-${tutorial.id}`}
                >
                  <Download className="h-4 w-4" />
                  {file.filename || 'Download'}
                </a>
              )}
              {tutorial.type === 'video' && videoUrl && !embedUrl && (
                <a
                  href={videoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-sm text-primary hover:underline font-medium"
                >
                  <ExternalLink className="h-4 w-4" />
                  Watch Video
                </a>
              )}
            </div>
            {isAdmin && (
              <div className="flex gap-1 flex-shrink-0">
                <Button size="sm" variant="ghost" onClick={() => openEditModal(tutorial)} data-testid={`edit-tutorial-${tutorial.id}`}>
                  <Edit className="h-4 w-4" />
                </Button>
                <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700" onClick={() => handleDelete(tutorial.id)} data-testid={`delete-tutorial-${tutorial.id}`}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  const TutorialSection = ({ title, icon: Icon, tutorials: sectionTutorials, audience }) => (
    <div className="mb-12">
      <div className="flex items-center gap-3 mb-6">
        <div className={`p-2.5 rounded-lg ${audience === 'attendee' ? 'bg-blue-50' : 'bg-orange-50'}`}>
          <Icon className={`h-6 w-6 ${audience === 'attendee' ? 'text-blue-600' : 'text-orange-600'}`} />
        </div>
        <div>
          <h2 className="text-xl font-bold text-slate-900">{title}</h2>
          <p className="text-sm text-slate-500">
            {audience === 'attendee' ? t('knowledge.attendee_desc') : t('knowledge.exhibitor_desc')}
          </p>
        </div>
      </div>
      {sectionTutorials.length === 0 ? (
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-8 text-center">
          <BookOpen className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">{t('knowledge.no_tutorials')}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {sectionTutorials.map(tutorial => (
            <TutorialCard key={tutorial.id} tutorial={tutorial} />
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-6xl mx-auto px-4 py-10 sm:py-14">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <GraduationCap className="h-8 w-8 text-primary" />
                <h1 className="text-3xl sm:text-4xl font-bold text-slate-900">{t('knowledge.title')}</h1>
              </div>
              <p className="text-slate-600 max-w-2xl">{t('knowledge.subtitle')}</p>
            </div>
            {isAdmin && (
              <Button
                onClick={() => { setEditingTutorial(null); setForm(emptyForm); setShowModal(true); }}
                data-testid="add-tutorial-btn"
              >
                <Plus className="h-4 w-4 mr-2" /> {t('knowledge.add_tutorial')}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
          </div>
        ) : (
          <>
            <TutorialSection
              title={t('knowledge.for_attendees')}
              icon={Users}
              tutorials={attendeeTutorials}
              audience="attendee"
            />
            <TutorialSection
              title={t('knowledge.for_exhibitors')}
              icon={Store}
              tutorials={exhibitorTutorials}
              audience="exhibitor"
            />
          </>
        )}
      </div>

      {/* Admin Add/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingTutorial ? 'Edit Tutorial' : 'Add Tutorial'}</DialogTitle>
            <DialogDescription>
              {editingTutorial ? 'Update the tutorial details below.' : 'Add a new tutorial for attendees or exhibitors.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5 py-2">
            {/* Audience & Type */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Audience</Label>
                <Select value={form.audience} onValueChange={(val) => setForm(prev => ({ ...prev, audience: val }))}>
                  <SelectTrigger data-testid="tutorial-audience-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="attendee">For Attendees</SelectItem>
                    <SelectItem value="exhibitor">For Exhibitors</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Type</Label>
                <Select value={form.type} onValueChange={(val) => setForm(prev => ({ ...prev, type: val }))}>
                  <SelectTrigger data-testid="tutorial-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="document">Document (PDF/PPT)</SelectItem>
                    <SelectItem value="video">Video (YouTube)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Display Order */}
            <div>
              <Label>Display Order (lower = first)</Label>
              <Input
                type="number"
                value={form.order}
                onChange={(e) => setForm(prev => ({ ...prev, order: parseInt(e.target.value) || 0 }))}
                data-testid="tutorial-order-input"
              />
            </div>

            {/* Multi-language Title & Description */}
            <div className="space-y-4">
              <Label className="text-base font-semibold flex items-center gap-2"><Globe className="h-4 w-4" /> Title & Description per Language</Label>
              {LANGUAGES.map(lang => (
                <div key={lang.code} className="border rounded-lg p-4 space-y-3">
                  <p className="text-sm font-medium">{lang.flag} {lang.name}</p>
                  <div>
                    <Label className="text-xs">Title</Label>
                    <Input
                      value={form.title[lang.code] || ''}
                      onChange={(e) => setForm(prev => ({ ...prev, title: { ...prev.title, [lang.code]: e.target.value } }))}
                      placeholder={`Title in ${lang.name}`}
                      data-testid={`tutorial-title-${lang.code}`}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Description</Label>
                    <Textarea
                      value={form.description[lang.code] || ''}
                      onChange={(e) => setForm(prev => ({ ...prev, description: { ...prev.description, [lang.code]: e.target.value } }))}
                      placeholder={`Description in ${lang.name}`}
                      rows={2}
                      data-testid={`tutorial-desc-${lang.code}`}
                    />
                  </div>
                </div>
              ))}
            </div>

            {/* Document upload per language */}
            {form.type === 'document' && (
              <div className="space-y-4">
                <Label className="text-base font-semibold flex items-center gap-2"><FileText className="h-4 w-4" /> Upload Document per Language</Label>
                {LANGUAGES.map(lang => (
                  <div key={lang.code} className="flex items-center gap-3 border rounded-lg p-3">
                    <span className="text-sm font-medium min-w-[100px]">{lang.flag} {lang.name}</span>
                    {form.files[lang.code] ? (
                      <div className="flex items-center gap-2 flex-1">
                        <span className="text-sm text-green-600 truncate">{form.files[lang.code].filename}</span>
                        <Button size="sm" variant="ghost" className="text-red-500" onClick={() => setForm(prev => { const f = { ...prev.files }; delete f[lang.code]; return { ...prev, files: f }; })}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    ) : (
                      <Button size="sm" variant="outline" onClick={() => handleFileUpload(lang.code)} disabled={uploading} data-testid={`upload-file-${lang.code}`}>
                        <Upload className="h-3 w-3 mr-1" /> {uploading ? 'Uploading...' : 'Upload'}
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Video URLs per language */}
            {form.type === 'video' && (
              <div className="space-y-4">
                <Label className="text-base font-semibold flex items-center gap-2"><Video className="h-4 w-4" /> YouTube URL per Language</Label>
                {LANGUAGES.map(lang => (
                  <div key={lang.code} className="flex items-center gap-3 border rounded-lg p-3">
                    <span className="text-sm font-medium min-w-[100px]">{lang.flag} {lang.name}</span>
                    <Input
                      className="flex-1"
                      value={form.video_urls[lang.code] || ''}
                      onChange={(e) => setForm(prev => ({ ...prev, video_urls: { ...prev.video_urls, [lang.code]: e.target.value } }))}
                      placeholder={`https://www.youtube.com/watch?v=...`}
                      data-testid={`video-url-${lang.code}`}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button onClick={handleSave} data-testid="save-tutorial-btn">
              {editingTutorial ? 'Update' : 'Create'} Tutorial
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default KnowledgePage;
